import {
  AlertCircle, CloudOff, ShieldAlert, UserX, type LucideIcon,
} from 'lucide-react';

export type SignInErrorView = {
  icon: LucideIcon;
  severity: 'destructive' | 'warning';
  title: string;
  description: string;
  action?: { label: string; href: string };
};

const OAUTH_RESTRICTION_DOCS_URL =
  'https://docs.github.com/articles/restricting-access-to-your-organization-s-data';
const SSO_DOCS_URL =
  'https://docs.github.com/authentication/authenticating-with-saml-single-sign-on/authorizing-an-oauth-app-for-use-with-saml-single-sign-on';
const APP_INSTALL_DOCS_URL =
  'https://docs.github.com/apps/using-github-apps/installing-a-github-app-from-a-third-party';

const ORGANIZATION_PATTERN = /membership check failed for '([^']+)'/;
const ALLOWED_ORGANIZATIONS_PATTERN = /restricted to members of:\s*(.+?)\.?$/;
const APP_NOT_INSTALLED_PATTERN = /GitHub App is not installed for:\s*(.+?)\.?$/;
const INACTIVE_MEMBERSHIP_PATTERN =
  /requires an active membership of '([^']+)'/;
const EMBEDDED_URL_PATTERN = /https?:\/\/[^\s)]+/;

const firstMatch = (message: string, pattern: RegExp): string | undefined =>
  message.match(pattern)?.[1]?.trim();

export const describeSignInError = (error: unknown): SignInErrorView => {
  const message =
    error instanceof Error ? error.message : String(error ?? '').trim();
  const organization = firstMatch(message, ORGANIZATION_PATTERN);
  const target = organization ? `the ${organization} organisation` : 'this organisation';

  if (/OAuth App access restrictions/i.test(message)) {
    return {
      icon: ShieldAlert,
      severity: 'warning',
      title: 'Portal access needs organisation approval',
      description: `Your account signed in successfully, but ${target} restricts which applications can read its data, and the Developer Portal has not been approved yet. Ask an organisation owner to approve it, then try again.`,
      action: {
        label: 'How owners approve an application',
        href: message.match(EMBEDDED_URL_PATTERN)?.[0] ?? OAUTH_RESTRICTION_DOCS_URL,
      },
    };
  }

  if (/single sign-on|saml|\bsso\b/i.test(message)) {
    return {
      icon: ShieldAlert,
      severity: 'warning',
      title: 'Single sign-on authorisation required',
      description: `Your session has not been authorised for ${target} through SAML single sign-on. Complete the SSO prompt on GitHub, then try again.`,
      action: { label: 'Authorise a session for SSO', href: SSO_DOCS_URL },
    };
  }

  const appNotInstalledFor = firstMatch(message, APP_NOT_INSTALLED_PATTERN);
  if (appNotInstalledFor) {
    return {
      icon: ShieldAlert,
      severity: 'warning',
      title: 'The portal app is not installed on your organisation',
      description: `Your GitHub account was verified, but the Vivicta Developer Portal app is not installed on ${appNotInstalledFor}, or your account cannot access the installation. Ask an organisation owner to install it, then try again.`,
      action: {
        label: 'How owners install an application',
        href: APP_INSTALL_DOCS_URL,
      },
    };
  }

  if (/'Members' organization permission/i.test(message)) {
    return {
      icon: ShieldAlert,
      severity: 'warning',
      title: 'The portal app is missing a permission',
      description: `The portal could not read your membership of ${target} because its GitHub App lacks the "Members" organisation permission. Ask an organisation owner to approve it, then try again.`,
    };
  }

  const pendingOrganization = firstMatch(message, INACTIVE_MEMBERSHIP_PATTERN);
  if (pendingOrganization) {
    return {
      icon: UserX,
      severity: 'destructive',
      title: 'Your organisation membership is not active',
      description: `The portal only issues sessions to active members of ${pendingOrganization}. Accept your pending invitation on GitHub, then sign in again.`,
    };
  }

  const allowedOrganizations = firstMatch(message, ALLOWED_ORGANIZATIONS_PATTERN);
  if (allowedOrganizations) {
    return {
      icon: UserX,
      severity: 'destructive',
      title: 'Your account is not a member of the required organisation',
      description: `The portal only issues sessions to active members of ${allowedOrganizations}. Ask the platform team to add your GitHub account, then sign in again.`,
    };
  }

  if (/failed to fetch|networkerror|load failed|econnrefused/i.test(message)) {
    return {
      icon: CloudOff,
      severity: 'destructive',
      title: 'Cannot reach the portal backend',
      description:
        'The sign-in service did not respond. Check your network connection and try again — if it keeps failing, the portal may be temporarily unavailable.',
    };
  }

  return {
    icon: AlertCircle,
    severity: 'destructive',
    title: 'Sign-in could not be completed',
    description:
      'Something went wrong while establishing your session. Try again, and contact the platform team if the problem persists.',
  };
};
